/**
 * BNZ Cart Add Popup
 * Affiche une popup (image + CTA produit) lorsqu'un produit est ajouté au panier.
 *
 * Multi-blocs : chaque bloc de la section rend une popup autonome avec ses
 * propres produits déclencheurs. À l'ajout au panier, on ouvre la PREMIÈRE
 * popup dont les déclencheurs contiennent le produit ajouté ; si aucune ne
 * correspond, on ouvre la première popup marquée « carte par défaut ».
 * Une seule popup est visible à la fois.
 *
 * Déclencheurs :
 *   1. Événement `cart:item-added` émis par CartUtils (ajouts natifs du thème).
 *   2. Repli : interception fetch sur /cart/add pour les ajouts tiers (PageFly, etc.)
 *      qui ne passent pas par CartUtils. Un anti-rebond évite le double affichage.
 */
(function () {
  'use strict';

  var SESSION_PREFIX = 'bnz_cart_add_popup_shown_';
  var DEBOUNCE_MS = 1200;

  var popups = [];
  var visible = null;
  var lastTrigger = 0;
  var globalsBound = false;

  function splitAttr(el, name) {
    var raw = el.getAttribute(name) || '';
    var parts = raw.split(',');
    var out = [];
    for (var i = 0; i < parts.length; i++) {
      var value = parts[i].replace(/^\s+|\s+$/g, '');
      if (value) out.push(value);
    }
    return out;
  }

  function shownThisSession(id) {
    try { return sessionStorage.getItem(SESSION_PREFIX + id) === '1'; } catch (e) { return false; }
  }

  function markShown(id) {
    try { sessionStorage.setItem(SESSION_PREFIX + id, '1'); } catch (e) {}
  }

  function find(el) {
    for (var i = 0; i < popups.length; i++) {
      if (popups[i].el === el) return popups[i];
    }
    return null;
  }

  // --- Enregistrement d'une popup ------------------------------------------
  function setup(el) {
    var existing = find(el);
    if (existing) return existing;

    var id = el.getAttribute('data-block-id') || el.id;
    var popup = {
      id: id,
      el: el,
      productIds: splitAttr(el, 'data-trigger-product-ids'),
      handles: splitAttr(el, 'data-trigger-handles'),
      catchAll: el.getAttribute('data-catch-all') === 'true',
      frequency: el.getAttribute('data-frequency') || 'session',
      delay: parseInt(el.getAttribute('data-delay'), 10) || 0,
      closeSideCart: el.getAttribute('data-close-side-cart') === 'true',
      timer: null
    };

    var backdrop = el.querySelector('.bnz-cap__backdrop');
    var closeBtn = el.querySelector('.bnz-cap__close');
    if (backdrop) backdrop.addEventListener('click', function () { close(popup); });
    if (closeBtn) closeBtn.addEventListener('click', function () { close(popup); });

    popups.push(popup);
    return popup;
  }

  // Rescanne le DOM : gère le re-render de la section dans l'éditeur de thème
  // et les blocs ajoutés/supprimés à chaud.
  function collect() {
    var alive = [];
    for (var i = 0; i < popups.length; i++) {
      if (document.body.contains(popups[i].el)) {
        alive.push(popups[i]);
      } else {
        if (popups[i].timer) clearTimeout(popups[i].timer);
        if (visible === popups[i]) visible = null;
      }
    }
    popups = alive;

    var nodes = document.querySelectorAll('[data-bnz-cap]');
    for (var j = 0; j < nodes.length; j++) setup(nodes[j]);
  }

  // --- Sélection de la popup à ouvrir --------------------------------------
  function matches(popup, added) {
    if (!added) return false;
    if (added.product_id != null && popup.productIds.indexOf(String(added.product_id)) !== -1) return true;
    if (added.handle && popup.handles.indexOf(String(added.handle)) !== -1) return true;
    return false;
  }

  function pick(added) {
    var i, popup;

    // 1. Correspondance explicite. Si elle a déjà été vue cette session, on
    //    n'ouvre rien : la carte par défaut ne doit pas la remplacer.
    for (i = 0; i < popups.length; i++) {
      popup = popups[i];
      if (popup.catchAll && !popup.productIds.length && !popup.handles.length) continue;
      if (matches(popup, added)) {
        if (popup.frequency === 'session' && shownThisSession(popup.id)) return null;
        return popup;
      }
    }

    // 2. Repli : première carte par défaut disponible.
    for (i = 0; i < popups.length; i++) {
      popup = popups[i];
      if (!popup.catchAll) continue;
      if (popup.frequency === 'session' && shownThisSession(popup.id)) continue;
      return popup;
    }

    return null;
  }

  // --- Ouverture / fermeture -----------------------------------------------
  function open(popup, preview) {
    if (visible && visible !== popup) close(visible);

    // Évite le chevauchement avec le panier latéral qui s'ouvre à l'ajout
    if (popup.closeSideCart && window.CartUtils && typeof window.CartUtils.closeDrawer === 'function') {
      try { window.CartUtils.closeDrawer(); } catch (e) {}
    }

    popup.el.removeAttribute('hidden');
    popup.el.setAttribute('aria-hidden', 'false');
    // Forcer un reflow pour déclencher la transition d'entrée
    void popup.el.offsetHeight;
    popup.el.classList.add('is-visible');
    visible = popup;

    if (!preview && popup.frequency === 'session') markShown(popup.id);
    document.addEventListener('keydown', onEsc);
  }

  function close(popup) {
    popup.el.classList.remove('is-visible');
    popup.el.setAttribute('aria-hidden', 'true');
    if (visible === popup) visible = null;
    document.removeEventListener('keydown', onEsc);
    window.setTimeout(function () {
      if (!popup.el.classList.contains('is-visible')) popup.el.setAttribute('hidden', '');
    }, 350);
  }

  function onEsc(e) {
    if ((e.key === 'Escape' || e.keyCode === 27) && visible) close(visible);
  }

  function trigger(added) {
    var popup = pick(added);
    if (!popup) return;

    // Anti double-déclenchement APRÈS le filtre : déduplique le double-signal
    // (événement + repli fetch) du même ajout, sans qu'un ajout non-cible
    // « consomme » la fenêtre et bloque l'affichage d'une popup ciblée.
    var now = new Date().getTime();
    if (now - lastTrigger < DEBOUNCE_MS) return;
    lastTrigger = now;

    if (popup.timer) clearTimeout(popup.timer);
    if (popup.delay > 0) {
      popup.timer = window.setTimeout(function () { open(popup); }, popup.delay);
    } else {
      open(popup);
    }
  }

  // --- Écouteurs globaux (une seule fois) ----------------------------------
  function bindGlobals() {
    if (globalsBound) return;
    globalsBound = true;

    // Déclencheur principal : événement du thème
    document.addEventListener('cart:item-added', function (e) {
      var added = e && e.detail && e.detail.item ? e.detail.item : null;
      trigger(added);
    });

    // Repli : interception fetch pour les ajouts tiers
    if (typeof window.fetch === 'function') {
      var originalFetch = window.fetch;
      window.fetch = function () {
        var args = arguments;
        var url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url) || '';
        var method = (args[1] && args[1].method ? args[1].method : 'GET').toUpperCase();
        var isCartAdd = method === 'POST' && url.indexOf('/cart/add') !== -1;

        var result = originalFetch.apply(this, args);

        if (isCartAdd) {
          result.then(function (response) {
            if (!response || !response.ok) return;
            response.clone().json().then(function (data) {
              var added = data;
              if (data && data.items && data.items.length) added = data.items[0];
              trigger(added);
            }).catch(function () {
              trigger(null);
            });
          }).catch(function () {});
        }

        return result;
      };
    }

    // Éditeur de thème : re-render de la section, et prévisualisation de la
    // card quand le merchant sélectionne son bloc.
    document.addEventListener('shopify:section:load', collect);

    document.addEventListener('shopify:block:select', function (e) {
      var el = e.target;
      if (!el || !el.hasAttribute || !el.hasAttribute('data-bnz-cap')) return;
      collect();
      var popup = find(el);
      if (popup) open(popup, true);
    });

    document.addEventListener('shopify:block:deselect', function (e) {
      var el = e.target;
      if (!el || !el.hasAttribute || !el.hasAttribute('data-bnz-cap')) return;
      var popup = find(el);
      if (popup) close(popup);
    });
  }

  function init() {
    collect();
    bindGlobals();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
